// src/pages/Marketplace/shared/useSpotlight.ts
import { useCallback, useEffect, useState } from 'react';
import { ApiError, useAdminApi } from '@/pages/Admin/shared/adminApi';
import type { Env } from '@/pages/Admin/shared';
import type {
  SpotlightDesignationBody,
  SpotlightDesignationResult,
  SpotlightDesignationsResponse,
  SpotlightEligibleBusinessesResponse,
} from './types';

/**
 * Spotlight designation reads and writes.
 *
 * These take `env` and go through `useAdminApi` rather than the `(apiUrl,
 * accessToken)` signature the older Marketplace hooks use: the centralized
 * helper adds the non-prod WAF dev-key header and, more importantly, surfaces
 * the backend's error *code* on `ApiError.code`. The write endpoint returns a
 * distinct code per rejected gate (`business_not_enrolled`,
 * `week_already_started`, …), and the panel maps those to specific admin-facing
 * copy — a plain status text would collapse them all into "409".
 */

const SPOTLIGHT_PATH = '/admin/gamification/spotlight';

/** Human copy for every error code the spotlight endpoints can return. */
export function spotlightErrorMessage(e: unknown): string {
  const code = e instanceof ApiError ? e.code : undefined;
  switch (code) {
    case 'week_already_started':
      return 'That week has already started and can no longer be changed. Pick a later Monday.';
    case 'business_market_mismatch':
      return 'That venue is not assigned to this market anymore. Reassign it or pick another venue.';
    case 'business_not_approved':
      return 'That venue is not approved yet. Approve its business verification first.';
    case 'business_not_enrolled':
      return 'That venue is not enrolled in gamification.';
    case 'business_enrollment_inactive':
      return 'That venue’s gamification enrollment is inactive — it needs a live geofence and at least one active reward.';
    case 'business_not_found':
      return 'That venue no longer exists.';
    case 'market_not_found':
      return 'That market no longer exists.';
    case 'FORBIDDEN':
      return 'Your admin session is not authorized for this action. Sign in again.';
    case 'INVALID_JSON':
      return 'The request was malformed. Reload the page and try again.';
    default:
      return e instanceof Error ? e.message : 'Unknown error';
  }
}

/**
 * Current + upcoming designations for one market. The backend defaults the lower
 * bound to the running week, so the current (frozen) pick comes back alongside
 * what is already booked.
 */
export function useSpotlightDesignations(env: Env, marketId: string | null) {
  const { request } = useAdminApi();
  const [data, setData] = useState<SpotlightDesignationsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!marketId) { setData(null); return; }
    setLoading(true); setError(null);
    try {
      setData(await request<SpotlightDesignationsResponse>({
        env, path: SPOTLIGHT_PATH, query: { market_id: marketId, limit: 200 },
      }));
    } catch (e) {
      setError(spotlightErrorMessage(e));
    } finally { setLoading(false); }
  }, [request, env, marketId]);

  useEffect(() => { refetch(); }, [refetch]);

  return { data, loading, error, refetch };
}

/**
 * Venues in this market that would survive the write endpoint's gates. Fetched
 * lazily — the picker is behind a modal, and eligibility can change between
 * opening the panel and opening the modal.
 */
export function useSpotlightEligibleBusinesses(env: Env) {
  const { request } = useAdminApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEligible = useCallback(async (marketId: string) => {
    setLoading(true); setError(null);
    try {
      const body = await request<SpotlightEligibleBusinessesResponse>({
        env,
        path: `${SPOTLIGHT_PATH}/eligible-businesses`,
        query: { market_id: marketId },
      });
      return body.items;
    } catch (e) {
      setError(spotlightErrorMessage(e));
      return null;
    } finally { setLoading(false); }
  }, [request, env]);

  return { fetchEligible, loading, error };
}

/** Create or replace one (market, week) designation. */
export function useSpotlightMutation(env: Env) {
  const { request } = useAdminApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const designate = useCallback(async (
    body: SpotlightDesignationBody,
  ): Promise<SpotlightDesignationResult | null> => {
    setLoading(true); setError(null);
    try {
      return await request<SpotlightDesignationResult>({
        env, path: SPOTLIGHT_PATH, method: 'PUT', body,
      });
    } catch (e) {
      setError(spotlightErrorMessage(e));
      return null;
    } finally { setLoading(false); }
  }, [request, env]);

  return { designate, loading, error, clearError: () => setError(null) };
}
